import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useMemo } from 'react'
import { from } from 'rxjs'

import { userClaimIds } from '../../../utils/userClaims'
import { UserPrivacyQualifier } from '../types'
import { useUserUpdatePrivacyQualifiers } from './useUserUpdatePrivacyQualifiers'

const isGpcEnabled = typeof navigator !== 'undefined' && !!navigator.globalPrivacyControl

export const useUserPrivacyQualifiers = () => {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0()
  const [updateUserPrivacyQualifiers] = useUserUpdatePrivacyQualifiers()

  const privacyQualifiers = useMemo((): UserPrivacyQualifier[] | undefined => {
    if (!user) {
      return undefined
    }
    const { [userClaimIds.privacyQualifiers]: privacyQualifiersClaim } = user
    if (!privacyQualifiersClaim || !Array.isArray(privacyQualifiersClaim)) {
      return undefined
    }
    return privacyQualifiersClaim.filter((value): value is UserPrivacyQualifier => value === 'gpc')
  }, [user])

  useEffect(() => {
    if (isLoading) {
      return
    }
    if (isAuthenticated && privacyQualifiers?.length) {
      if (typeof window.gaDisable === 'function') {
        // will prevent gtag from tracking the user.
        window.gaDisable()
      }
    } else {
      if (typeof window.gaEnable === 'function') {
        // will allow gtag to track the user.
        window.gaEnable()
      }
    }
  }, [isAuthenticated, isLoading, privacyQualifiers])

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return
    }
    const hasGpcQualifier = !!privacyQualifiers && privacyQualifiers.indexOf('gpc') >= 0
    const addedQualifiers = isGpcEnabled && !hasGpcQualifier ? ['gpc' as const] : []
    if (addedQualifiers.length === 0) {
      return
    }
    const subscription = from(
      updateUserPrivacyQualifiers({
        privacy_qualifiers: [...(privacyQualifiers ?? []), ...addedQualifiers],
      }),
    ).subscribe({
      next: () => getAccessTokenSilently({ ignoreCache: true }),
      error: (err) => console.log(err),
    })
    return () => subscription.unsubscribe()
  }, [
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    privacyQualifiers,
    updateUserPrivacyQualifiers,
  ])
}
